import * as THREE from 'three';
import { cellCenter, gustLee, gustsAhead, hexToWorld, panPulls, panWeights, rimLayout, ropeLive, spanLayout, weightsLayout, worldToCell, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { companyShield, COMPANY_RED } from '../models/sendas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cut bridge (docs/sendas/guerrero.md, chapter 2): the gorge between two plates of the shell, drawn round the
 * Spine's snow (engine/spine.ts, which repaints the board and lights it, and asks this for the drop). What can't be
 * walked is the drop: the board's tiles and the ground beyond are cut away there, and the gorge goes down in walls
 * of blue-grey rock banded with snow, into mist, to the Furrows far below (a labyrinth of green ridges, in a shader,
 * hazed by depth). Each place:
 *
 * - `rim` (act 1, and the opening): this side's edge; the old bridge's two posts and winches, the span hanging into
 *   the drop and coming up as Kel winds; the hooks biting the edge, their ropes down the wall, shaking by what climbs
 *   them (a dark shape on the rope, the ogre's thick and stretching), falling when cut. The platform out in the
 *   gorge, far, with the bridge's other stump hanging from it.
 * - `span`, `spanFire` (act 2, its scene and the campfire): the rock platform in the middle of the gorge; Ari and Gerd
 *   kneeling frozen on its flank with their shields; the covers planted (planks and the Company's shields); the gust
 *   sheet: the wind's streaks along the gorge thickening as a gust comes, the snow lifting off the upwind lip, and
 *   where the lee is the snow lies still (a mask of cells, in the shader); the gorge's flag, a red banner and a pale
 *   pennant flying the next two gusts' ways.
 * - `weights` (act 3, the close): the platform's north lip; the gantry with three pans, their posts, the chains between
 *   them glowing with what they pass; the three spans out over the drop end to end, each tipping by its count (too
 *   heavy down, too light up), coming down level together; the pile of pieces; the serpent's rock cracking.
 * - `chasmFar` (the cinematic): the whole gorge from this side's edge: the rock in the middle, the bridge's stumps
 *   hanging from both, the Furrows below; high above (engine/spine.ts's far ranges), the pass with its ice split black.
 *
 * Draw calls: the walls and the dressing (one merged mesh each), the deep floor (one), the mist (three sheets), the
 * gust sheet (one), the props that move (a handful of groups), the ropes (one line set). No lights.
 */

export const CHASM_PLACES = new Set(['rim', 'span', 'spanFire', 'weights', 'chasmFar']);

const S = WORLD_PER_HEX;
const K = 1000 / S;
const ROW = 0.866 * S;
/** How deep the gorge's walls go, and the Furrows' floor below them. */
const DEEP = 34;
const FLOOR = -46;

const ROCK = ['#4a5361', '#3e4653', '#56606e', '#353c48'];
const SNOW = new THREE.Color('#e8edf4');
const SNOW_SHADE = new THREE.Color('#b9c4d3');
const HAZE = new THREE.Color('#7c8ba3');
const WOOD = ['#3a3430', '#4a4038', '#2e2925', '#5a4c40'];
const IRON = '#3d424a';
const BONE = '#d9d2c2';
const ROPE = '#7a6a52';

/** Value noise for looks only (no sim): smooth in 2D. */
const h2 = (x: number, y: number): number => {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
};
const n2 = (x: number, y: number): number => {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = h2(ix, iy);
  const b = h2(ix + 1, iy);
  const c = h2(ix, iy + 1);
  const d = h2(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
};

/** The Furrows far below: ridges in a maze of dark green, hazed by depth, a little mist moving over them. */
const floorMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    fog: false,
    uniforms: { uTime: { value: 0 }, uHaze: { value: HAZE.clone() } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying float vDist;
      void main() {
        vec4 w = modelMatrix * vec4(position, 1.0);
        vXZ = w.xz;
        vec4 mv = viewMatrix * w;
        vDist = -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uHaze;
      varying vec2 vXZ;
      varying float vDist;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        // The Furrows: long winding corridors of stone seen from very high up (a warped maze: each cell walled on one
        // side or the other by its hash), dark green in the corridors, paler on the ridges.
        vec2 q = vXZ * 0.11;
        q += vec2(n(q * 0.7 + 4.0), n(q * 0.7 - 2.0)) * 1.6;
        vec2 c = floor(q);
        vec2 f = fract(q);
        float r = h(c);
        float wx = step(0.5, r) * (1.0 - smoothstep(0.06, 0.14, abs(f.x - 0.5)));
        float wy = step(r, 0.5) * (1.0 - smoothstep(0.06, 0.14, abs(f.y - 0.5)));
        float edge = 1.0 - smoothstep(0.0, 0.07, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
        float ridge = max(max(wx, wy), edge * 0.8);
        vec3 low = vec3(0.04, 0.12, 0.08);
        vec3 top = vec3(0.22, 0.4, 0.26);
        vec3 col = mix(low, top, ridge) * (0.7 + 0.6 * n(vXZ * 0.05));
        // Mist drifting over them, and the depth's haze over everything (more the farther).
        float mist = smoothstep(0.35, 0.85, n(vXZ * 0.05 + vec2(uTime * 0.02, 0.0)));
        col = mix(col, uHaze * 0.85, mist * 0.45);
        float k = clamp((vDist - 40.0) / 80.0, 0.0, 1.0);
        col = mix(col, uHaze, 0.22 + 0.6 * k);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/** A sheet of mist in the gorge: soft billows drifting along it, thinning at the sheet's edges. */
const mistMat = (strength: number, tint: THREE.Color): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    fog: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uStrength: { value: strength }, uTint: { value: tint.clone() } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        vec4 w = modelMatrix * vec4(position, 1.0);
        vXZ = w.xz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uStrength;
      uniform vec3 uTint;
      varying vec2 vXZ;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 p = vXZ * 0.07 + vec2(uTime * 0.035, uTime * 0.006);
        float f = n(p) * 0.55 + n(p * 2.3 + 3.1) * 0.3 + n(p * 5.1 - 1.7) * 0.15;
        float edge = smoothstep(0.0, 0.2, vUv.x) * smoothstep(1.0, 0.8, vUv.x) * smoothstep(0.0, 0.2, vUv.y) * smoothstep(1.0, 0.8, vUv.y);
        float a = smoothstep(0.48, 0.86, f) * uStrength * edge;
        gl_FragColor = vec4(mix(uTint * 0.8, vec3(0.93, 0.95, 0.99), f), a);
      }`,
  });

/**
 * The windbreak's gusts over the platform: streaks of blown snow running along the gorge (`uDir`), thickening and
 * rising as a gust comes (`uWarn`, 0 to 1 over its warning) and whipping when it strikes (`uHit`); where the next gust's
 * lee is (a mask of the board's cells, `uLee`), the snow lies still. The cell under a point is found in the shader
 * (axial rounding), so the mask is the sim's own cells.
 */
const gustMat = (R: number): { mat: THREE.ShaderMaterial; tex: THREE.DataTexture; data: Uint8Array } => {
  const W = 2 * R + 1;
  const data = new Uint8Array(W * W * 4);
  const tex = new THREE.DataTexture(data, W, W, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
    uniforms: { uTime: { value: 0 }, uDir: { value: 1 }, uWarn: { value: 0 }, uHit: { value: 0 }, uLee: { value: tex }, uR: { value: R }, uHex: { value: S } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying float vFog;
      void main() {
        vec4 w = modelMatrix * vec4(position + normal * 0.035, 1.0);
        vXZ = w.xz;
        vec4 mv = viewMatrix * w;
        vFog = clamp(-mv.z / 90.0, 0.0, 1.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uDir;
      uniform float uWarn;
      uniform float uHit;
      uniform sampler2D uLee;
      uniform float uR;
      uniform float uHex;
      varying vec2 vXZ;
      varying float vFog;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        // The cell under this point (pointy-top axial, cube rounding): its lee from the mask.
        float rf = vXZ.y / (uHex * 0.8660254);
        float qf = vXZ.x / uHex - rf * 0.5;
        float sf = -qf - rf;
        float q = floor(qf + 0.5), r = floor(rf + 0.5), s = floor(sf + 0.5);
        float dq = abs(q - qf), dr = abs(r - rf), ds = abs(s - sf);
        if (dq > dr && dq > ds) q = -r - s; else if (dr > ds) r = -q - s;
        vec2 uv = (vec2(q, r) + uR + 0.5) / (2.0 * uR + 1.0);
        float lee = (abs(q) <= uR && abs(r) <= uR) ? texture2D(uLee, uv).r : 0.0;
        // Streaks along the gorge, sliding downwind; faster and thicker as the gust comes, whipping when it strikes.
        float a = vXZ.x * uDir;
        float b = vXZ.y;
        float t = uTime * (1.6 + uWarn * 2.4 + uHit * 3.0);
        float s1 = n(vec2(a * 0.5 - t * 2.4, b * 4.4 + sin(a * 0.3 + uTime) * 0.5));
        float s2 = n(vec2(a * 1.4 - t * 4.0, b * 9.0 + 5.0));
        float gust = 0.35 + 0.65 * n(vec2(a * 0.08 - t * 0.5, b * 0.08));
        float v = s1 * 0.7 + s2 * 0.45;
        float streak = smoothstep(0.62 - uWarn * 0.3, 0.96, v) * mix(gust, 1.0, uWarn * 0.6);
        float body = min(0.95, (0.42 + uWarn * 0.7 + uHit * 0.5) * streak);
        // Blown snow on white snow: a cool grey-blue body with a bright core, so it reads as moving drifts.
        float core = smoothstep(0.88, 1.02, v);
        vec3 col = mix(vec3(0.6, 0.68, 0.82), vec3(1.0), core * 0.85);
        // The lee: still snow, a cold shade so it reads as a place where the wind can't reach.
        float still = lee * (0.45 + uWarn * 0.55);
        float alpha = body * (1.0 - still) * (1.0 - vFog * 0.6);
        float shade = still * (0.16 + 0.14 * uWarn);
        col = mix(col, vec3(0.34, 0.45, 0.66), shade / max(alpha + shade, 0.001));
        gl_FragColor = vec4(col, max(alpha, shade));
      }`,
  });
  return { mat, tex, data };
};

/** A cloth flying downwind: a plane bent and waved in the vertex shader (the gorge's flag). */
export const flagMat = (color: THREE.ColorRepresentation): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uColor: { value: new THREE.Color(color) }, uWild: { value: 0.5 } },
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform float uWild;
      varying float vU;
      varying float vLight;
      void main() {
        vU = uv.x;
        vec3 p = position;
        float k = uv.x;
        float w = sin(k * 6.0 - uTime * (7.0 + uWild * 6.0)) * 0.12 * k * (0.6 + uWild);
        p.z += w;
        p.y += sin(k * 4.0 - uTime * 5.0) * 0.03 * k;
        vLight = 0.75 + 0.25 * cos(k * 6.0 - uTime * (7.0 + uWild * 6.0));
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      varying float vU;
      varying float vLight;
      void main() { gl_FragColor = vec4(uColor * vLight * (1.0 - vU * 0.15), 1.0); }`,
  });

interface HookView {
  rope: number;
  g: THREE.Group;
  line: THREE.Mesh;
  climber: THREE.Mesh;
  bit: number;
  cutAt: number;
  shakes: number;
  at: THREE.Vector3;
}

export class ChasmPlace {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private timed: THREE.ShaderMaterial[] = [];
  private now = 0;
  /** Is a world point (x, z) over the drop. */
  readonly voidAt: (x: number, z: number) => boolean;
  /** Where the HUD hangs its marks (world points). */
  readonly anchors: { hooks: THREE.Vector3[]; winch: THREE.Vector3 | null; pans: THREE.Vector3[]; posts: THREE.Vector3[]; chains: THREE.Vector3[]; pile: THREE.Vector3 | null; flag: THREE.Vector3 | null } = {
    hooks: [],
    winch: null,
    pans: [],
    posts: [],
    chains: [],
    pile: null,
    flag: null,
  };
  // The rim.
  private span: THREE.Group | null = null;
  private spanUp = 0;
  private hooks = new Map<number, HookView>();
  private hookGeo: { hook: THREE.BufferGeometry; rope: THREE.BufferGeometry; body: THREE.BufferGeometry } | null = null;
  private hookMats: { bone: THREE.Material; rope: THREE.Material; thick: THREE.Material; dark: THREE.Material } | null = null;
  // The windbreak.
  private gust: { mat: THREE.ShaderMaterial; tex: THREE.DataTexture; data: Uint8Array; key: string; warnAt: number; hitAt: number; dir: number } | null = null;
  private covers = new Map<number, THREE.Group>();
  private flags: { g: THREE.Group; mat: THREE.ShaderMaterial; yaw: number; want: number }[] = [];
  private spindrift: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  // The counterweight.
  private spans: { g: THREE.Group; tilt: number; mid: THREE.Vector3 }[] = [];
  private pans: { g: THREE.Group; drop: number; pieces: THREE.Group[] }[] = [];
  private pile: THREE.Group[] = [];
  private chainMats: THREE.MeshStandardMaterial[] = [];
  private crack: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; at: number } | null = null;
  private lowerAt = -1;
  private cineSpans = false;
  private thudDone = false;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
    kit: Kit,
  ) {
    this.group.name = `chasm-${place}`;
    const drop = new Set((map.rocks ?? []).map((h) => `${h.q},${h.r}`));
    const M = map.radius;
    const inMap = (x: number, z: number): boolean => worldToCell(M, Math.round(x * K), Math.round(z * K)) >= 0;
    const cellVoid = (x: number, z: number): boolean => {
      const id = worldToCell(M, Math.round(x * K), Math.round(z * K));
      if (id < 0) return false;
      const W = 2 * M + 1;
      const q = (id % W) - M;
      const r = Math.floor(id / W) - M;
      return drop.has(`${q},${r}`);
    };
    // Past the board the drop goes on by each place's own line.
    let outVoid: (x: number, z: number) => boolean;
    if (place === 'rim' || place === 'chasmFar') {
      const L = rimLayout(s.seed);
      const E = L.radius + 2;
      const edgeZ = (x: number): number => {
        const c = Math.max(-E, Math.min(E, Math.floor(x / S)));
        return ((L.edge[c + E] ?? -3) - 0.5) * ROW;
      };
      // The platform out in the gorge (seen from this side), past the span's reach.
      // The rock in the middle of the gorge across the drop, broad (its near face is what the edge looks at), the span
      // reaching it; past it the gorge goes on to the far plate.
      const out = place === 'chasmFar' ? PILLAR : SPAN_GAP;
      const across = (x: number, z: number): boolean => z < edgeZ(0) - out + (n2(x * 0.25, 4) - 0.5) * 2.2 && z > edgeZ(0) - out - 22 && Math.abs(x) < 11 + n2(z * 0.2, 1) * 3;
      outVoid = (x, z) => z < edgeZ(x) && !across(x, z) && z > edgeZ(x) - FAR_SIDE;
    } else if (place === 'span' || place === 'spanFire') {
      const L = spanLayout(s.seed);
      const R = L.radius;
      outVoid = (x, z) => {
        const half = 2.7 * S + n2(z * 0.4, 3) * 0.6 * S;
        // Past its ends: the raised span to the south, the three spans' lip to the north (the platform goes on as a
        // narrowing spine both ways, off the board).
        const len = (R - 0.5) * ROW;
        if (z > len) return Math.abs(x) > 0.9 * S && Math.abs(x) < 20 * S;
        if (z < -len) return Math.abs(x) > half * Math.max(0.3, 1 - (-len - z) / 6) && Math.abs(x) < 20 * S;
        return Math.abs(x) > half && Math.abs(x) < 20 * S;
      };
    } else {
      // Past the lip the drop runs the three spans' length to the far side's edge.
      const lip = -3.5 * ROW;
      const far = lip - 0.3 - 3 * (SPAN_LEN + 0.05) - 0.1;
      outVoid = (x, z) => (z < lip && z > far + (n2(x * 0.3, 2) - 0.5) * 1.4 * Math.min(1, Math.abs(x) / 3)) || (Math.abs(x) > 5.6 * S && Math.abs(x) < 20 * S && z < 12 * S && z > far);
    }
    // The platform's lips follow their own smooth line on the board too (cut by the cells, they showed every hex's
    // corner as a saw-tooth): the drawn edge wanders within half a hex of the walkable cells' edge.
    const smooth = place === 'span' || place === 'spanFire';
    this.voidAt = (x, z) => (inMap(x, z) && !smooth ? cellVoid(x, z) : outVoid(x, z));
    this.buildWalls(kit);
    this.buildDepths();
    if (place === 'rim' || place === 'chasmFar') this.buildRim(kit, s);
    if (place === 'span' || place === 'spanFire') this.buildSpan(kit, s);
    if (place === 'weights') this.buildWeights(kit, s);
  }

  private std(color?: THREE.ColorRepresentation, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.05, flatShading: true, ...(color !== undefined ? { color } : { vertexColors: true }), ...o });
    this.mats.push(m);
    return m;
  }

  /* ---------- the drop ---------- */

  /**
   * Cuts the drop out of the board's tiles and the ground beyond (their triangles over it folded to nothing), and the
   * outskirts' trees and rocks standing over it put away. Called after the snow is painted on.
   */
  carve(terrain: TerrainBuild): void {
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const m = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        let cz = 0;
        for (let k = 0; k < 3; k++) {
          v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(m);
          cx += v.x;
          cz += v.z;
        }
        const out = this.voidAt(cx / 3, cz / 3);
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      // On the platform the lip follows its smooth line: what is left of a tile past it is pulled in onto it (cut by
      // whole triangles, the tiles left a saw-tooth of hex corners along the drop).
      if (this.place === 'span' || this.place === 'spanFire') {
        const inv = m.clone().invert();
        for (let i = 0; i < pos.count; i++) {
          v.fromBufferAttribute(pos, i).applyMatrix4(m);
          if (!this.voidAt(v.x, v.z)) continue;
          let lo = 0;
          let hi = 1;
          for (let k = 0; k < 10; k++) {
            const mid = (lo + hi) / 2;
            if (this.voidAt(v.x * mid, v.z)) hi = mid;
            else lo = mid;
          }
          // Only near the lip (a vertex far out belongs to a triangle already gone).
          if (lo < 0.75) continue;
          v.x *= lo;
          v.applyMatrix4(inv);
          pos.setXYZ(i, v.x, v.y, v.z);
        }
      }
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    const mm = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') cut(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh) return;
      let touched = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, mm);
        p.setFromMatrixPosition(mm);
        // A tree or a rock near the lip goes too (it would hang over the edge).
        if (this.voidAt(p.x, p.z) || this.voidAt(p.x + 0.6, p.z) || this.voidAt(p.x - 0.6, p.z) || this.voidAt(p.x, p.z + 0.6) || this.voidAt(p.x, p.z - 0.6)) {
          im.setMatrixAt(i, zero);
          touched = true;
        }
      }
      if (touched) im.instanceMatrix.needsUpdate = true;
    });
  }

  /**
   * The gorge's walls: along every edge between ground and drop (found on a fine grid, marching squares), a face of
   * rock from the lip down into the mist, bulging and banded, snow caught on its upper ledges; teeth of rock along the
   * lip to break the tiles' line. One merged mesh.
   */
  private buildWalls(kit: Kit): void {
    const step = 0.3;
    const ext = this.place === 'chasmFar' ? 46 : 30;
    const n = Math.ceil((ext * 2) / step);
    const x0 = -ext;
    const z0 = -ext;
    const pos: number[] = [];
    const col: number[] = [];
    const c = new THREE.Color();
    const rock = new THREE.Color();
    const ground = (x: number, z: number): number => this.ctx.ground(x, z);
    const vx = new Float32Array((n + 1) * (n + 1));
    for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) vx[j * (n + 1) + i] = this.voidAt(x0 + i * step, z0 + j * step) ? 1 : 0;
    const at = (i: number, j: number): number => vx[j * (n + 1) + i] as number;
    const bands = 12;
    const faces: number[] = [];
    const face = (ax: number, az: number, bx: number, bz: number): void => {
      // Outward (toward the drop) normal, to push the face out a little as it goes down (an overhang here and there).
      const mx = (ax + bx) / 2;
      const mz = (az + bz) / 2;
      let ox = -(bz - az);
      let oz = bx - ax;
      const len = Math.hypot(ox, oz) || 1;
      ox /= len;
      oz /= len;
      if (!this.voidAt(mx + ox * 0.2, mz + oz * 0.2)) {
        ox = -ox;
        oz = -oz;
      }
      faces.push(mx, mz, ox, oz);
      const ga = Math.min(ground(ax, az), 0.4);
      const gb = Math.min(ground(bx, bz), 0.4);
      for (let k = 0; k < bands; k++) {
        const t0 = k / bands;
        const t1 = (k + 1) / bands;
        const y = (t: number, g: number): number => g - 0.05 - (DEEP + g) * (t * t * 0.6 + t * 0.4);
        // Ribs and gullies down the face, ledges across it, an overhang now and then.
        const bulge = (x: number, z: number, t: number): number =>
          (n2(x * 0.55 + t * 2.5, z * 0.55) - 0.5) * 2.2 * Math.min(1, t * 3) + (n2(x * 1.7, z * 1.7 + t * 9) - 0.5) * 0.35 + Math.sin(t * 14 + x * 0.6 + z * 0.4) * 0.16;
        const pa0 = bulge(ax, az, t0);
        const pb0 = bulge(bx, bz, t0);
        const pa1 = bulge(ax, az, t1);
        const pb1 = bulge(bx, bz, t1);
        const A0 = [ax + ox * pa0, y(t0, ga), az + oz * pa0];
        const B0 = [bx + ox * pb0, y(t0, gb), bz + oz * pb0];
        const A1 = [ax + ox * pa1, y(t1, ga), az + oz * pa1];
        const B1 = [bx + ox * pb1, y(t1, gb), bz + oz * pb1];
        pos.push(...A0, ...A1, ...B0, ...B0, ...A1, ...B1);
        // Strata: bands of rock, snow caught on the upper ledges, all of it going to the depth's haze.
        // Bands of rock down the face (lighter and darker strata), each face's own grain across them.
        const strata = Math.floor(n2(mx * 0.15 + t0 * 0.5, t0 * 7.5) * 4);
        rock.set(ROCK[strata % ROCK.length] as string).multiplyScalar(0.9 + (k % 3 === 1 ? 0.18 : 0) - t0 * 0.25 + (n2(mx * 2.3, mz * 2.3 + k) - 0.5) * 0.2);
        // Snow caught on the upper ledges in streaks, more of it near the lip.
        const snowy = k === 0 && n2(mx * 1.1, mz * 1.1) > 0.62;
        if (snowy) c.copy(SNOW_SHADE).multiplyScalar(0.85);
        else c.copy(rock);
        // Down into the haze: dark rock first, the mist's blue only deep down.
        c.lerp(HAZE, Math.min(0.8, Math.max(0, t0 - 0.25) * 1.2));
        for (let q = 0; q < 6; q++) col.push(c.r, c.g, c.b);
      }
    };
    // Marching squares over the void mask: each cell's boundary segments.
    for (let j = 0; j < n; j++)
      for (let i = 0; i < n; i++) {
        const a = at(i, j);
        const b = at(i + 1, j);
        const cc = at(i + 1, j + 1);
        const d = at(i, j + 1);
        const code = a | (b << 1) | (cc << 2) | (d << 3);
        if (code === 0 || code === 15) continue;
        const X = x0 + i * step;
        const Z = z0 + j * step;
        const e: Record<string, [number, number]> = {
          t: [X + step / 2, Z],
          r: [X + step, Z + step / 2],
          b: [X + step / 2, Z + step],
          l: [X, Z + step / 2],
        };
        const segs: [string, string][] =
          code === 1 || code === 14 ? [['l', 't']] : code === 2 || code === 13 ? [['t', 'r']] : code === 3 || code === 12 ? [['l', 'r']] : code === 4 || code === 11 ? [['r', 'b']] : code === 6 || code === 9 ? [['t', 'b']] : code === 7 || code === 8 ? [['l', 'b']] : code === 5 ? [['l', 't'], ['r', 'b']] : [['t', 'r'], ['l', 'b']];
        for (const [p, q] of segs) {
          const P = e[p] as [number, number];
          const Q = e[q] as [number, number];
          face(P[0], P[1], Q[0], Q[1]);
        }
      }
    // Buttresses and fallen blocks down the faces: clusters of broken rock on the walls, darker with depth.
    const block = new THREE.DodecahedronGeometry(1, 0);
    const bp = block.toNonIndexed();
    block.dispose();
    const bpos = bp.getAttribute('position') as THREE.BufferAttribute;
    const m4 = new THREE.Matrix4();
    const v3 = new THREE.Vector3();
    for (let i = 0; i < faces.length; i += 4 * 7) {
      const x = faces[i] as number;
      const z = faces[i + 1] as number;
      const ox = faces[i + 2] as number;
      const oz = faces[i + 3] as number;
      if (h2(x * 1.3, z * 1.7) < 0.45) continue;
      for (let d = 0; d < 2; d++) {
        const t = 0.12 + d * 0.3 + h2(x, z + d) * 0.12;
        const y = -(DEEP * (t * t * 0.6 + t * 0.4));
        const sc = 0.35 + h2(z, x + d) * 0.6;
        const out = (0.2 + t * 1.6) * (0.6 + h2(x + d, z));
        m4.compose(new THREE.Vector3(x + ox * out, y, z + oz * out), new THREE.Quaternion().setFromEuler(new THREE.Euler(h2(x, d), h2(z, d) * 6, h2(d, x))), new THREE.Vector3(sc * 1.1, sc * 1.9, sc));
        rock.set(ROCK[(d + Math.floor(x)) & 3] as string).multiplyScalar(0.8 - t * 0.25).lerp(HAZE, Math.min(0.8, Math.max(0, t - 0.25) * 1.2));
        for (let q = 0; q < bpos.count; q++) {
          v3.fromBufferAttribute(bpos, q).applyMatrix4(m4);
          pos.push(v3.x, v3.y, v3.z);
          col.push(rock.r, rock.g, rock.b);
        }
      }
    }
    bp.dispose();
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    g.computeVertexNormals();
    this.geos.push(g);
    const wall = new THREE.Mesh(g, this.std(undefined, { side: THREE.DoubleSide, fog: true }));
    wall.name = 'chasm-walls';
    wall.receiveShadow = true;
    wall.frustumCulled = false;
    this.group.add(wall);
    // Teeth of rock along the lip (over the board's tiles' zigzag): dark, broken, leaning out over the drop.
    const tooth = new THREE.DodecahedronGeometry(0.5, 0);
    const lump = new THREE.SphereGeometry(0.5, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
    let k = 0;
    for (let j = 0; j < n; j += 3)
      for (let i = 0; i < n; i += 3) {
        if (at(i, j) !== 0) continue;
        const near = at(Math.min(n, i + 2), j) || at(Math.max(0, i - 2), j) || at(i, Math.min(n, j + 2)) || at(i, Math.max(0, j - 2));
        if (!near) continue;
        const x = x0 + i * step + (h2(i, j) - 0.5) * 0.3;
        const z = z0 + j * step + (h2(j, i) - 0.5) * 0.3;
        k++;
        const r = h2(x * 3.1, z * 2.7);
        if (r < 0.35) continue;
        const g0 = this.ctx.ground(x, z);
        const sc = 0.22 + r * 0.38;
        kit.add(tooth, ROCK[k % ROCK.length] as string, place(x, g0 - 0.08, z, r * 6, new THREE.Vector3(sc * 1.3, sc * 0.55, sc), r - 0.5, 0.3 - r * 0.4));
        if (r > 0.85) kit.add(lump, SNOW_SHADE, place(x, g0 + sc * 0.12, z, r * 3, new THREE.Vector3(sc * 0.9, sc * 0.18, sc * 0.7)));
      }
    tooth.dispose();
    lump.dispose();
  }

  /** The Furrows' floor far below, and three sheets of mist between it and the lip. */
  private buildDepths(): void {
    const fm = floorMat();
    this.mats.push(fm);
    this.timed.push(fm);
    const fg = new THREE.PlaneGeometry(260, 260, 1, 1);
    this.geos.push(fg);
    const floor = new THREE.Mesh(fg, fm);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = FLOOR;
    floor.renderOrder = -2;
    floor.frustumCulled = false;
    this.group.add(floor);
    const mg = new THREE.PlaneGeometry(200, 200, 1, 1);
    this.geos.push(mg);
    const layers = this.ctx.low ? [-10, -24] : [-8, -17, -28];
    layers.forEach((y, i) => {
      const m = mistMat(0.5 - i * 0.08, HAZE);
      this.mats.push(m);
      this.timed.push(m);
      const sheet = new THREE.Mesh(mg, m);
      sheet.rotation.x = -Math.PI / 2;
      sheet.position.y = y;
      sheet.renderOrder = 1;
      sheet.frustumCulled = false;
      this.group.add(sheet);
    });
  }

  /* ---------- act 1: this side's edge ---------- */

  private w(x: number, y: number, h = 0): THREE.Vector3 {
    return this.ctx.at(x, y, h);
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.w(c.x, c.y, h);
  }

  /** The old bridge's posts and winches at the edge; the span (hanging, coming up); the platform far out. */
  private buildRim(kit: Kit, s: BattleState): void {
    const L = rimLayout(s.seed);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const [p0, p1] = L.posts.map((h) => this.hex(h.q, h.r)) as [THREE.Vector3, THREE.Vector3];
    // The posts: squared timbers, iron bands, a cap; a winch drum at each foot with its crank.
    for (const p of [p0, p1]) {
      kit.add(box, WOOD[0] as string, place(p.x, p.y + 1.1, p.z + 0.15, 0, new THREE.Vector3(0.32, 2.2, 0.32)));
      kit.add(box, WOOD[2] as string, place(p.x, p.y + 2.25, p.z + 0.15, 0, new THREE.Vector3(0.44, 0.12, 0.44)));
      for (const y of [0.5, 1.4, 2.0]) kit.add(box, IRON, place(p.x, p.y + y, p.z + 0.15, 0, new THREE.Vector3(0.35, 0.06, 0.35)));
      kit.add(cyl, WOOD[1] as string, place(p.x, p.y + 0.42, p.z + 0.85, 0, new THREE.Vector3(0.5, 0.75, 0.5), 0, Math.PI / 2));
      kit.add(box, IRON, place(p.x + 0.42, p.y + 0.42, p.z + 0.85, 0, new THREE.Vector3(0.05, 0.36, 0.05)));
      kit.add(box, WOOD[3] as string, place(p.x, p.y + 0.12, p.z + 0.85, 0, new THREE.Vector3(0.7, 0.24, 0.5)));
      // Snow caught on top of each.
      kit.add(box, SNOW, place(p.x, p.y + 2.33, p.z + 0.15, 0, new THREE.Vector3(0.4, 0.05, 0.4)));
    }
    const mid = p0.clone().lerp(p1, 0.5);
    this.anchors.winch = mid.clone().setY(mid.y + 0.4).setZ(mid.z + 0.85);
    // The span: a deck of planks with two rails, hinged at the edge between the posts.
    const span = new THREE.Group();
    span.position.set(mid.x, mid.y - 0.05, (L.edge[L.radius + 2] ?? -3) * ROW - 0.45 * ROW);
    const deck = new Kit();
    const len = SPAN_GAP + 0.6;
    for (let i = 0; i < 14; i++) {
      const z = -0.2 - (i / 14) * len;
      deck.add(box, WOOD[i % 4] as string, place((h2(i, 2) - 0.5) * 0.06, 0, z, (h2(i, 4) - 0.5) * 0.05, new THREE.Vector3(1.7, 0.08, len / 14 - 0.03)));
    }
    for (const sx of [-0.82, 0.82]) {
      deck.add(box, WOOD[2] as string, place(sx, 0.12, -len / 2, 0, new THREE.Vector3(0.1, 0.14, len)));
      for (let i = 0; i <= 4; i++) deck.add(box, WOOD[1] as string, place(sx, 0.45, -0.3 - (i / 4) * (len - 0.6), 0, new THREE.Vector3(0.07, 0.7, 0.07)));
      deck.add(box, ROPE, place(sx, 0.78, -len / 2, 0, new THREE.Vector3(0.04, 0.04, len)));
    }
    // Snow on the planks, and an old torn end where it was cut.
    deck.add(box, SNOW_SHADE, place(0, 0.05, -len * 0.4, 0, new THREE.Vector3(1.3, 0.03, len * 0.5)));
    const dm = deck.build(this.std(), 'chasm-span');
    if (dm) {
      dm.castShadow = true;
      this.geos.push(dm.geometry);
      span.add(dm);
    }
    // Its two lifting ropes, from the far end up to the posts' tops (redrawn as it comes up).
    this.group.add(span);
    this.span = span;
    this.spanRopes = [p0.clone().setY(p0.y + 2.2).setZ(p0.z + 0.15), p1.clone().setY(p1.y + 2.2).setZ(p1.z + 0.15)].map((top) => {
      const g = new THREE.BufferGeometry().setFromPoints([top, top.clone()]);
      this.geos.push(g);
      const l = new THREE.Line(g, this.lineMat(ROPE));
      l.frustumCulled = false;
      this.group.add(l);
      return { line: l, top };
    });
    this.setSpan(0);
    // The hooks' parts, made once.
    this.hookGeo = { hook: this.hookShape(), rope: new THREE.CylinderGeometry(0.025, 0.025, 1, 5), body: new THREE.CapsuleGeometry(0.16, 0.4, 3, 6) };
    this.geos.push(this.hookGeo.hook, this.hookGeo.rope, this.hookGeo.body);
    this.hookMats = { bone: this.std(BONE, { roughness: 0.7 }), rope: this.std(ROPE, { roughness: 1 }), thick: this.std('#5a4a36', { roughness: 1 }), dark: this.std('#12141a', { roughness: 1, flatShading: false }) };
    this.anchors.hooks = (s.rules?.senda?.hooks?.anchors ?? []).map((a) => this.w(a.x, a.y, 0.35));
    // The platform far out in the gorge, its snow, the bridge's other stump hanging off its near end, Ari and Gerd
    // tiny on it.
    // From afar, the bridge's other stump hanging off the platform's near end.
    if (this.place === 'chasmFar') {
      const pz = (L.edge[L.radius + 2] ?? -3) * ROW - 0.5 * ROW - PILLAR;
      for (let i = 0; i < 7; i++) kit.add(box, WOOD[i % 4] as string, place(0, -0.4 - i * 0.6, pz + 0.1 + i * 0.06, 0, new THREE.Vector3(1.6, 0.07, 0.55), 1.25 - i * 0.04));
    }
    box.dispose();
    cyl.dispose();
  }

  private spanRopes: { line: THREE.Line; top: THREE.Vector3 }[] = [];
  private lineMats = new Map<string, THREE.LineBasicMaterial>();
  private lineMat(color: string): THREE.LineBasicMaterial {
    let m = this.lineMats.get(color);
    if (!m) {
      m = new THREE.LineBasicMaterial({ color });
      this.mats.push(m);
      this.lineMats.set(color, m);
    }
    return m;
  }

  /** The span at this much of the way up (0 hanging into the drop, 1 level with the edge). */
  private setSpan(k: number): void {
    const sp = this.span;
    if (!sp) return;
    this.spanUp = k;
    // Hanging: nearly straight down; up: level.
    sp.rotation.x = -(1 - k) * 1.35;
    sp.updateMatrixWorld(true);
    const far = new THREE.Vector3();
    this.spanRopes.forEach((r, i) => {
      far.set(i === 0 ? -0.82 : 0.82, 0.78, -(SPAN_GAP + 0.4)).applyMatrix4(sp.matrixWorld);
      const a = r.line.geometry.getAttribute('position') as THREE.BufferAttribute;
      a.setXYZ(0, r.top.x, r.top.y, r.top.z);
      a.setXYZ(1, far.x, far.y, far.z);
      a.needsUpdate = true;
    });
  }

  /** A bone hook: a curved shank with a barb, lathed. */
  private hookShape(): THREE.BufferGeometry {
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i <= 8; i++) pts.push(new THREE.Vector2(0.04 + Math.sin((i / 8) * Math.PI) * 0.05, i * 0.05));
    const shank = new THREE.LatheGeometry(pts, 6);
    const barb = new THREE.ConeGeometry(0.06, 0.22, 5);
    barb.rotateX(Math.PI / 2 + 0.6);
    barb.translate(0, 0.38, 0.12);
    const both = [shank.toNonIndexed(), barb.toNonIndexed()];
    shank.dispose();
    barb.dispose();
    const out = new THREE.BufferGeometry();
    const p: number[] = [];
    for (const g of both) {
      p.push(...(g.getAttribute('position').array as Float32Array));
      g.dispose();
    }
    out.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
    out.computeVertexNormals();
    return out;
  }

  /** A rope that bit: its hook on the lip, the rope down the wall, what climbs it. */
  private addHook(rope: number, at: THREE.Vector3, shakes: number): HookView {
    const G = this.hookGeo as NonNullable<ChasmPlace['hookGeo']>;
    const Mt = this.hookMats as NonNullable<ChasmPlace['hookMats']>;
    const g = new THREE.Group();
    // At the lip: north from the anchor's cell to where the drop begins.
    const lip = at.clone();
    for (let k = 0; k < 30 && !this.voidAt(lip.x, lip.z - 0.05); k++) lip.z -= 0.05;
    g.position.set(lip.x, this.ctx.ground(lip.x, lip.z + 0.1), lip.z);
    // Toward the drop (north).
    // A bone grapple bitten into the lip: its three prongs curled into the snow on the land's side, the shank over
    // the edge, the rope from it down the wall (from the edge's height the wall is under the lip: what shows is the
    // grapple jerking with what climbs, and the rope's first stretch over the edge).
    const big = shakes >= 3 ? 2.3 : 1.6;
    const hook = new THREE.Group();
    hook.scale.setScalar(big);
    const shank = new THREE.Mesh(G.rope, Mt.bone);
    shank.scale.set(3.2, 0.75, 3.2);
    shank.rotation.x = 0.9;
    shank.position.set(0, -0.12, -0.18);
    hook.add(shank);
    for (const a of [-0.75, 0, 0.75]) {
      const prong = new THREE.Mesh(G.hook, Mt.bone);
      prong.scale.set(0.8, 0.8, 0.8);
      prong.position.set(Math.sin(a) * 0.08, 0.12, 0.06);
      prong.rotation.set(-1.9, a, 0);
      hook.add(prong);
    }
    g.add(hook);
    const lipRope = new THREE.Mesh(G.rope, shakes >= 3 ? Mt.thick : Mt.rope);
    lipRope.scale.set(shakes >= 3 ? 3.2 : 1.8, 2.4, shakes >= 3 ? 3.2 : 1.8);
    lipRope.rotation.x = 0.35;
    lipRope.position.set(0, -1.35, -0.62);
    g.add(lipRope);
    const line = lipRope;
    const climber = new THREE.Mesh(G.body, Mt.dark);
    climber.visible = false;
    g.add(climber);
    this.group.add(g);
    const v: HookView = { rope, g, line, climber, bit: this.now, cutAt: -1, shakes, at: at.clone() };
    this.hooks.set(rope, v);
    return v;
  }

  /* ---------- act 2: the platform in the wind ---------- */

  private buildSpan(kit: Kit, s: BattleState): void {
    const L = spanLayout(s.seed);
    // Ari and Gerd: kneeling where they cut the bridge, frozen hard, each behind a planted shield turned to the wind;
    // rime on helms and shoulders, the cloaks stiff with ice, icicles off their hems, snow drifted against their backs.
    const frost = '#8ea2b6';
    const cloak = '#5d6f84';
    const steel = '#a3afbd';
    const rime = '#e6eef8';
    L.fixed.forEach((hx, i) => {
      const p = this.hex(hx.q, hx.r);
      const g = new THREE.Group();
      g.position.copy(p);
      // Facing the wind (their side, −side along x), turned a little toward the gorge's south so their crosses read.
      g.rotation.y = (L.side < 0 ? -Math.PI / 2 : Math.PI / 2) + (L.side < 0 ? 0.5 : -0.5) * (i === 0 ? 1 : 0.7);
      const sh = companyShield(g, 1.3);
      sh.position.set(0, 0.36, 0.42);
      sh.rotation.x = 0.16;
      const fig = new Kit();
      const box = new THREE.BoxGeometry(1, 1, 1);
      const sph = new THREE.SphereGeometry(0.5, 10, 7);
      const cone = new THREE.ConeGeometry(0.5, 1, 9, 1, true);
      // Down on one knee: thigh and shin of the forward leg, the back knee on the snow.
      fig.add(box, frost, place(0.1, 0.22, 0.12, 0, new THREE.Vector3(0.13, 0.13, 0.3)));
      fig.add(box, frost, place(0.1, 0.12, 0.26, 0, new THREE.Vector3(0.12, 0.24, 0.1)));
      fig.add(box, frost, place(-0.1, 0.06, -0.06, 0, new THREE.Vector3(0.12, 0.1, 0.3)));
      // The body leaning into the shield, the cloak hanging stiff behind and down to the snow.
      fig.add(box, frost, place(0, 0.48, 0.06, 0, new THREE.Vector3(0.3, 0.38, 0.2), 0.28));
      fig.add(cone, cloak, place(0, 0.38, -0.06, 0, new THREE.Vector3(0.62, 0.72, 0.5), 0.15));
      fig.add(sph, rime, place(0, 0.64, -0.02, 0, new THREE.Vector3(0.36, 0.1, 0.26)));
      // Arms forward to the shield's rim.
      for (const k of [-1, 1]) fig.add(box, cloak, place(k * 0.16, 0.5, 0.22, 0, new THREE.Vector3(0.08, 0.08, 0.3), -0.35));
      // The helm, bowed: a steel cap with a nasal, rime on its crown.
      fig.add(sph, steel, place(0, 0.76, 0.12, 0, new THREE.Vector3(0.2, 0.2, 0.2)));
      fig.add(box, steel, place(0, 0.72, 0.22, 0, new THREE.Vector3(0.03, 0.1, 0.03)));
      fig.add(sph, rime, place(0, 0.84, 0.1, 0, new THREE.Vector3(0.17, 0.06, 0.16)));
      // Icicles off the cloak's hem, snow drifted against the back.
      const ic = new THREE.ConeGeometry(0.5, 1, 5);
      for (let k = 0; k < 5; k++) fig.add(ic, '#d6e4f2', place(-0.2 + k * 0.1, 0.07, -0.26, 0, new THREE.Vector3(0.04, 0.12, 0.04), Math.PI));
      fig.add(sph, SNOW, place(0, 0.06, -0.38, 0, new THREE.Vector3(0.7, 0.2, 0.36)));
      ic.dispose();
      const m = fig.build(this.std(undefined, { roughness: 0.5, metalness: 0.15 }), `chasm-frozen-${i}`);
      if (m) {
        m.castShadow = true;
        this.geos.push(m.geometry);
        g.add(m);
      }
      box.dispose();
      sph.dispose();
      cone.dispose();
      this.group.add(g);
    });
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The gorge's flag: a tall pole at the platform's middle, off the way on the lee flank; a red banner (the next gust)
    // and a pale pennant under it (the one after).
    const fx = -L.side * 2.0 * S;
    const fz = 0.2 * ROW;
    const fg = this.ctx.ground(fx, fz);
    kit.add(box, WOOD[0] as string, place(fx, fg + 1.9, fz, 0, new THREE.Vector3(0.09, 3.8, 0.09)));
    kit.add(box, IRON, place(fx, fg + 3.82, fz, 0, new THREE.Vector3(0.16, 0.06, 0.16)));
    for (let k = 0; k < 4; k++) kit.add(box, ROCK[k] as string, place(fx + Math.cos(k * 1.6) * 0.25, fg + 0.15, fz + Math.sin(k * 1.6) * 0.25, k, new THREE.Vector3(0.35, 0.3, 0.3)));
    this.anchors.flag = new THREE.Vector3(fx, fg + 4.3, fz);
    for (const [i, color, len, y] of [
      [0, COMPANY_RED, 1.5, 3.55],
      [1, '#c8d0dc', 1.0, 2.95],
    ] as const) {
      const geo = new THREE.PlaneGeometry(len, i === 0 ? 0.62 : 0.32, 12, 2);
      geo.translate(len / 2, 0, 0);
      this.geos.push(geo);
      const mat = flagMat(color);
      this.mats.push(mat);
      this.timed.push(mat);
      const g = new THREE.Group();
      g.position.set(fx, fg + y, fz);
      const cloth = new THREE.Mesh(geo, mat);
      cloth.castShadow = true;
      g.add(cloth);
      this.group.add(g);
      this.flags.push({ g, mat, yaw: 0, want: 0 });
    }
    // The gust sheet over the board (its lee mask the sim's cells), and the snow lifting off the upwind lip.
    const R = this.map.radius;
    this.gust = { ...gustMat(R), key: '', warnAt: -1, hitAt: -1, dir: 1 };
    this.mats.push(this.gust.mat);
    // The campfire's hearth (its scene): a ring of stones on the rock, split logs crossed in it, charred.
    if (this.place === 'spanFire') {
      const f = this.hex(-4, 7);
      const st = new THREE.DodecahedronGeometry(0.5, 0);
      for (let k = 0; k < 9; k++) {
        const a = (k / 9) * Math.PI * 2;
        kit.add(st, ROCK[k % 4] as string, place(f.x + Math.cos(a) * 0.38, f.y + 0.06, f.z + Math.sin(a) * 0.38, k, 0.16));
      }
      for (let k = 0; k < 4; k++) kit.add(box, k % 2 ? '#2a1e16' : '#4a3426', place(f.x, f.y + 0.1 + k * 0.03, f.z, k * 0.8, new THREE.Vector3(0.55, 0.07, 0.08)));
      kit.add(box, '#ff6a24', place(f.x, f.y + 0.16, f.z, 0.4, new THREE.Vector3(0.2, 0.05, 0.2)));
      st.dispose();
    }
    // The raised span from act 1 at the south end.
    const len = (L.radius - 0.5) * ROW;
    for (let i = 0; i < 12; i++) kit.add(box, WOOD[i % 4] as string, place(0, -0.06, len + 0.4 + i * 0.6, 0, new THREE.Vector3(1.7, 0.08, 0.55)));
    for (const sx of [-0.82, 0.82]) kit.add(box, WOOD[2] as string, place(sx, 0.06, len + 3.8, 0, new THREE.Vector3(0.1, 0.14, 7.2)));
    box.dispose();
    this.wantSpindrift();
  }

  /** The snow lifting off the upwind lip as a gust comes: a curtain along the platform's flank, in a shader. */
  private wantSpindrift(): void {
    const geo = new THREE.PlaneGeometry(1, 1, 1, 1);
    geo.translate(0, 0.5, 0);
    this.geos.push(geo);
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: { uTime: { value: 0 }, uWarn: { value: 0 }, uDir: { value: 1 } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vW;
        void main() { vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform float uWarn;
        uniform float uDir;
        varying vec2 vUv;
        varying vec3 vW;
        float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
        void main() {
          float t = uTime;
          float f = n(vec2(vW.z * 1.2, vUv.y * 3.0 - t * 2.5)) * 0.6 + n(vec2(vW.z * 3.1 + t, vUv.y * 7.0 - t * 4.0)) * 0.4;
          float a = smoothstep(0.45, 0.9, f) * (1.0 - vUv.y) * smoothstep(0.0, 0.08, vUv.x) * smoothstep(1.0, 0.92, vUv.x);
          gl_FragColor = vec4(vec3(0.95, 0.97, 1.0), a * uWarn * 0.85);
        }`,
    });
    this.mats.push(mat);
    this.timed.push(mat);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = 5;
    mesh.frustumCulled = false;
    mesh.visible = false;
    this.group.add(mesh);
    this.spindrift = { mesh, mat };
  }

  /** The gust sheet over the board, laid on the snowfield (SpineLayer gives its ground). */
  gustSheet(src: THREE.Mesh): void {
    if (!this.gust) return;
    const sheet = new THREE.Mesh(src.geometry, this.gust.mat);
    sheet.matrixAutoUpdate = false;
    src.updateMatrixWorld(true);
    sheet.matrix.copy(src.matrixWorld);
    sheet.renderOrder = 3;
    sheet.name = 'chasm-gust';
    sheet.frustumCulled = false;
    this.group.add(sheet);
  }

  /** A cover planted on a cell: a plank (propped, roped) or one of the Company's shields, its face to the wind. */
  private cover(cell: number, shield: boolean, dir: number): THREE.Group {
    const c = cellCenter(this.map.radius, cell);
    const p = this.w(c.x, c.y);
    const g = new THREE.Group();
    g.position.copy(p);
    // Face the side the wind comes from (−dir along x), turned a little toward the gorge's south so the camera reads it.
    g.rotation.y = dir > 0 ? -Math.PI / 2 + 0.55 : Math.PI / 2 - 0.55;
    if (shield) {
      const sh = companyShield(g, 1.7);
      sh.position.set(0, 0.48, 0.1);
      sh.rotation.x = 0.12;
    } else {
      const k = new Kit();
      const box = new THREE.BoxGeometry(1, 1, 1);
      for (let i = 0; i < 3; i++) k.add(box, WOOD[(cell + i) % 4] as string, place(-0.28 + i * 0.28, 0.45, 0.08, 0, new THREE.Vector3(0.26, 0.92 - (i % 2) * 0.08, 0.06), 0.08));
      k.add(box, WOOD[2] as string, place(0, 0.62, 0.12, 0, new THREE.Vector3(0.9, 0.08, 0.05)));
      k.add(box, WOOD[1] as string, place(0, 0.32, -0.22, 0, new THREE.Vector3(0.07, 0.7, 0.07), -0.7));
      k.add(box, ROPE, place(0, 0.62, 0.15, 0, new THREE.Vector3(0.92, 0.03, 0.03)));
      box.dispose();
      const m = k.build(this.std(), 'chasm-plank');
      if (m) {
        m.castShadow = true;
        this.geos.push(m.geometry);
        g.add(m);
      }
    }
    this.group.add(g);
    return g;
  }

  /* ---------- act 3: the counterweight ---------- */

  private buildWeights(kit: Kit, s: BattleState): void {
    const L = weightsLayout();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 14);
    const lip = -3.5 * ROW;
    // The gantry over the lip: two tall legs and a beam, the rule carved on it (lines of dark marks).
    const gy = 4.6;
    for (const sx of [-5.2, 5.2]) {
      kit.add(box, WOOD[0] as string, place(sx, gy / 2, lip + 0.5, 0, new THREE.Vector3(0.42, gy, 0.42)));
      kit.add(box, WOOD[2] as string, place(sx, 0.2, lip + 0.5, 0, new THREE.Vector3(0.8, 0.4, 0.8)));
      kit.add(box, WOOD[1] as string, place(sx * 0.82, gy * 0.55, lip + 0.5, 0, new THREE.Vector3(0.14, 2.2, 0.14), 0, sx > 0 ? 0.6 : -0.6));
    }
    kit.add(box, WOOD[1] as string, place(0, gy, lip + 0.5, 0, new THREE.Vector3(11, 0.34, 0.36)));
    kit.add(box, SNOW, place(0, gy + 0.2, lip + 0.5, 0, new THREE.Vector3(10.6, 0.06, 0.3)));
    for (let i = 0; i < 2; i++) for (let k = 0; k < 14; k++) kit.add(box, '#2a2622', place(-3.2 + k * 0.5, gy - 0.05 - i * 0.12, lip + 0.69, 0, new THREE.Vector3(0.3, 0.03, 0.01)));
    // The pans: iron dishes hung from the beam on four chains, a post beside each with its number (the HUD's).
    const pans = L.pans.map((h) => this.hex(h.q, h.r));
    pans.forEach((p, i) => {
      const g = new THREE.Group();
      g.position.set(p.x, p.y + 1.0, p.z + 0.25);
      const dish = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.42, 0.18, 16, 1, true), this.std('#6a717c', { metalness: 0.35, roughness: 0.55, side: THREE.DoubleSide }));
      this.geos.push(dish.geometry);
      g.add(dish);
      const bottom = new THREE.Mesh(new THREE.CircleGeometry(0.42, 16), this.std('#4a5058', { metalness: 0.3 }));
      this.geos.push(bottom.geometry);
      bottom.rotation.x = -Math.PI / 2;
      bottom.position.y = -0.08;
      g.add(bottom);
      for (let k = 0; k < 4; k++) {
        const a = (k / 4) * Math.PI * 2 + 0.4;
        const ch = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 1, 4), this.std(IRON));
        this.geos.push(ch.geometry);
        ch.position.set(Math.cos(a) * 0.3, (gy - p.y - 1.0) / 2, Math.sin(a) * 0.3);
        ch.scale.y = gy - p.y - 1.0;
        ch.rotation.z = Math.cos(a) * 0.08;
        ch.rotation.x = -Math.sin(a) * 0.08;
        g.add(ch);
      }
      this.group.add(g);
      this.pans.push({ g, drop: 0, pieces: [] });
      this.anchors.pans.push(new THREE.Vector3(p.x, p.y + 1.4, p.z + 0.25));
      // The post: a squared timber beside the pan, its number on a plate (the HUD draws the number).
      const px = p.x + 0.95;
      kit.add(box, WOOD[3] as string, place(px, 0.9, p.z + 0.4, 0, new THREE.Vector3(0.24, 1.8, 0.24)));
      kit.add(box, '#cfd5dd', place(px, 1.55, p.z + 0.53, 0, new THREE.Vector3(0.36, 0.36, 0.02)));
      // The post's number cut into its plate, dark on pale (a texture of its own, drawn once).
      const post = s.rules?.senda?.weight?.posts[i];
      if (post !== undefined) {
        const cv = document.createElement('canvas');
        cv.width = cv.height = 64;
        const g2 = cv.getContext('2d');
        if (g2) {
          g2.fillStyle = '#cfd5dd';
          g2.fillRect(0, 0, 64, 64);
          g2.fillStyle = '#1a1c22';
          g2.font = 'bold 44px Georgia, serif';
          g2.textAlign = 'center';
          g2.textBaseline = 'middle';
          g2.fillText(String(post), 32, 35);
        }
        const tex = new THREE.CanvasTexture(cv);
        tex.colorSpace = THREE.SRGBColorSpace;
        this.textures.push(tex);
        const pm = new THREE.MeshBasicMaterial({ map: tex });
        this.mats.push(pm);
        const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.34), pm);
        this.geos.push(plate.geometry);
        plate.position.set(px, 1.55, p.z + 0.545);
        this.group.add(plate);
      }
      this.anchors.posts.push(new THREE.Vector3(px, 2.05, p.z + 0.53));
      // The pulley over it, on the beam.
      kit.add(cyl, IRON, place(p.x, gy - 0.25, lip + 0.5, 0, new THREE.Vector3(0.4, 0.12, 0.4), Math.PI / 2));
      void i;
    });
    // The cross chains between the pans (middle to each outer one), glowing with what they pass.
    for (const i of [0, 2]) {
      const a = pans[1] as THREE.Vector3;
      const b = pans[i] as THREE.Vector3;
      const mat = new THREE.MeshStandardMaterial({ color: '#5a606a', metalness: 0.6, roughness: 0.45, emissive: '#000000' });
      this.mats.push(mat);
      this.chainMats.push(mat);
      const len = a.distanceTo(b) - 1.2;
      const links = new Kit();
      const ring = new THREE.TorusGeometry(0.06, 0.016, 4, 8);
      const n = Math.floor(len / 0.1);
      for (let k = 0; k < n; k++) {
        const t = (k + 0.5) / n;
        const x = THREE.MathUtils.lerp(a.x, b.x, t) + (b.x > a.x ? -0.6 + 1.2 * t : 0.6 - 1.2 * t) * 0;
        const sag = Math.sin(t * Math.PI) * 0.35;
        links.add(ring, '#ffffff', place(THREE.MathUtils.lerp(a.x + (b.x > a.x ? 0.6 : -0.6), b.x + (b.x > a.x ? -0.6 : 0.6), t), 1.0 - sag + a.y, a.z + 0.25, 0, 1, 0, k % 2 ? Math.PI / 2 : 0));
        void x;
      }
      ring.dispose();
      const geo = links.build(new THREE.MeshBasicMaterial(), 'chain')?.geometry;
      if (geo) {
        this.geos.push(geo);
        const m = new THREE.Mesh(geo, mat);
        this.group.add(m);
      }
      this.anchors.chains.push(new THREE.Vector3((a.x + b.x) / 2, a.y + 0.35, a.z + 0.25));
    }
    // The three spans out over the drop, end to end, each on two stays from the gantry's top.
    const spanLen = SPAN_LEN;
    for (let i = 0; i < 3; i++) {
      const mid = new THREE.Vector3(SPAN_X, -0.05, lip - 0.3 - spanLen / 2 - i * (spanLen + 0.05));
      const g = new THREE.Group();
      g.position.copy(mid);
      const deck = new Kit();
      for (let k = 0; k < 9; k++) deck.add(box, WOOD[(k + i) % 4] as string, place((h2(k, i) - 0.5) * 0.05, 0, -spanLen / 2 + (k + 0.5) * (spanLen / 9), (h2(i, k) - 0.5) * 0.04, new THREE.Vector3(1.7, 0.08, spanLen / 9 - 0.03)));
      for (const sx of [-0.82, 0.82]) {
        deck.add(box, WOOD[2] as string, place(sx, 0.12, 0, 0, new THREE.Vector3(0.1, 0.14, spanLen)));
        deck.add(box, IRON, place(sx, 0.2, spanLen / 2 - 0.05, 0, new THREE.Vector3(0.14, 0.14, 0.14)));
      }
      deck.add(box, SNOW_SHADE, place(0, 0.05, 0, 0, new THREE.Vector3(1.2, 0.03, spanLen * 0.6)));
      const dm = deck.build(this.std(), `chasm-span-${i}`);
      if (dm) {
        dm.castShadow = true;
        this.geos.push(dm.geometry);
        g.add(dm);
      }
      this.group.add(g);
      this.spans.push({ g, tilt: 0, mid });
    }
    // Their stays (redrawn as they tip), from the gantry's top to each span's two ends.
    this.stays = this.spans.map((sp) => {
      const geo = new THREE.BufferGeometry().setFromPoints(new Array(4).fill(0).map(() => new THREE.Vector3()));
      this.geos.push(geo);
      const l = new THREE.LineSegments(geo, this.lineMat('#3a3d44'));
      l.frustumCulled = false;
      this.group.add(l);
      void sp;
      return l;
    });
    this.gantryTop = new THREE.Vector3(SPAN_X, gy + 0.1, lip + 0.5);
    // The pile: the stretcher, the five shields and the two stones (drawn by where each piece is).
    const pile = this.hex(L.pile.q, L.pile.r);
    this.anchors.pile = pile.clone().setY(pile.y + 0.9);
    for (const sx of [-0.3, 0.3]) kit.add(box, WOOD[1] as string, place(pile.x + sx, pile.y + 0.08, pile.z, 0, new THREE.Vector3(0.06, 0.06, 1.9)));
    for (let k = 0; k < 5; k++) kit.add(box, WOOD[k % 3] as string, place(pile.x, pile.y + 0.12, pile.z - 0.6 + k * 0.3, 0, new THREE.Vector3(0.66, 0.03, 0.12)));
    const pieces = s.rules?.senda?.weight?.pieces ?? [];
    const stone = new THREE.DodecahedronGeometry(0.19, 0);
    this.geos.push(stone);
    pieces.forEach((pc) => {
      const g = new THREE.Group();
      if (pc.stone) {
        const m = new THREE.Mesh(stone, this.std('#6c7480'));
        m.castShadow = true;
        g.add(m);
        // Its weight cut into it: two notches.
        const nt = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.03, 0.03), this.std('#2a2e34'));
        this.geos.push(nt.geometry);
        nt.position.set(0, 0.22, 0.1);
        g.add(nt);
      } else {
        const sh = companyShield(g, 0.62 + pc.w * 0.06);
        sh.rotation.x = -Math.PI / 2 + 0.15;
      }
      this.group.add(g);
      this.pile.push(g);
    });
    // The rock cracking where the serpent comes up: a dark star of cracks, growing.
    const cg = new THREE.CircleGeometry(1, 24);
    this.geos.push(cg);
    const cm = new THREE.MeshBasicMaterial({ color: '#0a0d12', transparent: true, opacity: 0, depthWrite: false });
    this.mats.push(cm);
    const crack = new THREE.Mesh(cg, cm);
    crack.rotation.x = -Math.PI / 2;
    crack.renderOrder = 4;
    crack.visible = false;
    this.group.add(crack);
    this.crack = { mesh: crack, mat: cm, at: -1 };
    box.dispose();
    cyl.dispose();
  }

  private stays: THREE.LineSegments[] = [];
  private textures: THREE.Texture[] = [];
  /** Reused every frame (nothing new per frame, docs/RENDIMIENTO.md). */
  private liveSet = new Set<number>();
  private coversTick = -1;
  private tmp = new THREE.Vector3();
  private gantryTop = new THREE.Vector3();

  /** The counterweight in a cinematic: the spans coming down together as it opens. */
  lowerSpans(): void {
    this.cineSpans = true;
    this.lowerAt = -2;
  }

  /* ---------- the cinematic ---------- */

  /* ---------- as the fight goes ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.w(e.x, e.y);
      if (e.kind === 'hook') {
        this.ctx.shake(0.06);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.15), { n: 8, color: '#e6ecf4', speed: 0.05, up: 0.05, size: 0.1, life: 14, gravity: 0.004, spread: 0.3 });
      } else if (e.kind === 'cut') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.2), { n: 12, color: '#c9b28c', speed: 0.06, up: 0.04, size: 0.08, life: 16, gravity: 0.005, spread: 0.3 });
      } else if (e.kind === 'climb') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: e.n === 3 ? 22 : 8, color: '#dfe6f0', speed: 0.07, up: 0.06, size: 0.14, life: 18, gravity: 0.004, spread: 0.5 });
        if (e.n === 3) this.ctx.shake(0.25);
      } else if (e.kind === 'gustWarn' && this.gust) {
        this.gust.warnAt = this.now;
        this.gust.dir = e.n ?? 1;
      } else if (e.kind === 'gust' && this.gust) {
        this.gust.hitAt = this.now;
        this.gust.warnAt = -1;
        this.ctx.shake(0.12);
      } else if (e.kind === 'knock') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: 14, color: '#f2f6ff', speed: 0.08, up: 0.05, size: 0.14, life: 20, gravity: 0.004, spread: 0.6 });
      } else if (e.kind === 'plant' || e.kind === 'pull' || e.kind === 'set' || e.kind === 'pick' || e.kind === 'haul') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.2), { n: this.ctx.low ? 5 : 10, color: '#f2f6ff', speed: 0.04, up: 0.05, size: 0.1, life: 16, gravity: 0.003, spread: 0.35 });
      } else if (e.kind === 'crack' && this.crack) {
        this.crack.at = this.now;
        this.crack.mesh.position.set(p.x, this.ctx.ground(p.x, p.z) + 0.04, p.z);
        this.crack.mesh.visible = true;
      } else if (e.kind === 'rise') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: this.ctx.low ? 14 : 30, color: '#6b7380', speed: 0.09, up: 0.09, size: 0.18, life: 26, gravity: 0.006, spread: 0.7 });
        if (this.crack) this.crack.at = -2;
      } else if (e.kind === 'throw') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.0), { n: 12, color: '#ff8a6e', speed: 0.05, up: 0.03, size: 0.1, life: 18, gravity: 0.004, spread: 0.3 });
      } else if (e.kind === 'sink') {
        if (this.crack) this.crack.at = -2;
      } else if (e.kind === 'level') {
        this.ctx.shake(0.2);
      }
    }
  }

  draw(s: BattleState): void {
    const st = s.senda;
    const r = s.rules?.senda;
    // The rim: the span coming up; the hooks.
    if (r?.hooks && st?.hooks) {
      const k = Math.min(1, st.hooks.wound / Math.max(1, r.hooks.winch));
      if (Math.abs(k - this.spanUp) > 0.002) this.setSpan(k);
      const live = this.liveSet;
      live.clear();
      st.hooks.ropes.forEach((x) => {
        const p = r.hooks?.plan[x.i];
        if (!p) return;
        const idx = x.i;
        let v = this.hooks.get(idx);
        if (!v) {
          const a = this.anchors.hooks[p.at];
          if (!a) return;
          v = this.addHook(idx, a, p.shakes);
        }
        live.add(idx);
        if (x.cut && v.cutAt < 0) v.cutAt = this.now;
        const done = !ropeLive(r.hooks as NonNullable<typeof r.hooks>, x);
        const t = this.now - v.bit;
        if (v.cutAt >= 0) {
          // Cut: the rope falls away into the mist; the grapple stays, slack.
          const f = this.now - v.cutAt;
          v.line.position.y = -1.35 - f * f * 9;
          v.line.visible = f < 1.2;
          v.g.rotation.z = 0;
        } else if (!done) {
          // Jerking by what climbs it: one, two or three pulls a beat (the ogre's harder, its rope stretching).
          const beat = (t * 1.25) % 1;
          const pulse = Math.max(0, Math.sin(beat * Math.PI * 2 * p.shakes)) * (beat < 0.62 ? 1 : 0);
          const k = p.shakes >= 3 ? 1.7 : 1;
          v.g.rotation.x = -pulse * 0.09 * k;
          v.line.rotation.z = Math.sin(this.now * 17 + idx) * 0.02 * pulse * k;
          v.line.scale.y = 2.4 + pulse * 0.15 * k;
        } else {
          v.climber.visible = false;
          v.g.rotation.z = 0;
        }
      });
      for (const [i, v] of this.hooks) {
        if (live.has(i)) continue;
        this.group.remove(v.g);
        this.hooks.delete(i);
      }
    }
    // The windbreak: the covers, the flag, the gust's lee in the snow.
    if (r?.gust && st?.gust) {
      const next = gustsAhead(s, 2);
      const dir = next[0]?.dir ?? this.gust?.dir ?? 1;
      // The covers change only on a tick (a plant, a pull): looked at then, not every frame.
      if (s.tick !== this.coversTick) {
        this.coversTick = s.tick;
        const seen = this.liveSet;
        seen.clear();
        st.gust.cells.forEach((c, i) => {
          seen.add(c);
          if (!this.covers.has(c)) this.covers.set(c, this.cover(c, st.gust?.kinds[i] === 1, dir));
        });
        for (const [c, g] of this.covers) {
          if (seen.has(c)) continue;
          this.group.remove(g);
          this.covers.delete(c);
        }
      }
      // The flag: the banner flies toward where the next gust blows, the pennant the one after.
      this.flags.forEach((f, i) => {
        const g = next[i];
        f.want = g ? (g.dir > 0 ? 0 : Math.PI) : f.want;
      });
      if (this.gust) {
        const key = `${st.gust.cells.join(',')}|${dir}`;
        if (key !== this.gust.key) {
          this.gust.key = key;
          const lee = gustLee(s, dir);
          this.gust.data.fill(0);
          for (const c of lee) this.gust.data[c * 4] = 255;
          this.gust.tex.needsUpdate = true;
          this.gust.mat.uniforms.uDir!.value = dir;
        }
      }
    }
    // The counterweight: the pans down by their weight, the spans tipping by their count, the pieces where they are.
    if (r?.weight && st?.weight) {
      const R = r.weight;
      const W = st.weight;
      const pulls = this.cineSpans ? R.posts : panPulls(R, W);
      const ws = panWeights(R, W);
      this.spans.forEach((sp, i) => {
        // Too heavy: its far end down; too light: up (a little per point, at most a third of a turn).
        let want = Math.max(-0.42, Math.min(0.42, ((pulls[i] as number) - (R.posts[i] as number)) * 0.07));
        // The cinematic: held tipped a moment, then down together, slowly, the chains taking the weight.
        if (this.cineSpans) {
          const k = this.lowerAt >= 0 ? Math.min(1, Math.max(0, (this.now - this.lowerAt - 1.2) / 3.2)) : 0;
          want = (i === 1 ? -0.34 : 0.3) * (1 - k * k * (3 - 2 * k));
          sp.tilt = want;
          if (k > 0.98 && !this.thudDone) {
            this.thudDone = true;
            this.ctx.shake(0.35);
            for (const q of this.spans) this.ctx.vfx.burst(q.mid.clone().setY(0.3), { n: 18, color: '#eef3fa', speed: 0.07, up: 0.05, size: 0.18, life: 26, gravity: 0.004, spread: 1.4, smoke: true });
          }
        }
        sp.tilt += (want - sp.tilt) * 0.12;
        sp.g.rotation.x = sp.tilt;
      });
      this.pans.forEach((p, i) => {
        p.drop += (Math.min(0.5, (ws[i] as number) * 0.035) - p.drop) * 0.15;
        p.g.position.y = (this.anchors.pans[i] as THREE.Vector3).y - 0.4 - p.drop;
      });
      // The chains: lit by what they pass (the middle's half to each outer one), dark when nothing.
      const half = (ws[1] as number) >> 1;
      // (Iron, lit from inside by what it passes: a glow, not paint.)
      for (const m of this.chainMats) m.emissive.set(half > 0 ? '#ff4f3a' : '#000000').multiplyScalar(half > 0 ? 0.28 + Math.min(0.2, half * 0.03) + 0.06 * Math.sin(this.now * 3) : 0);
      // The pieces: on the pile, on a pan, in the hero's hands, hanging under the lip, or gone.
      const pile = this.anchors.pile as THREE.Vector3;
      const onPan = [0, 0, 0];
      const hero = s.heroes[0];
      const hp = hero ? this.ctx.pos(hero.id) : undefined;
      W.on.forEach((o, i) => {
        const g = this.pile[i];
        if (!g) return;
        g.visible = o !== -4;
        if (o === -1) g.position.set(pile.x - 0.25 + (i % 4) * 0.17, pile.y - 0.68 + Math.floor(i / 4) * 0.12 + (i % 2) * 0.04, pile.z - 0.5 + i * 0.16);
        else if (o >= 0 && o <= 2) {
          const pg = this.pans[o]?.g;
          if (pg) g.position.set(pg.position.x + (onPan[o] as number) * 0.05 - 0.05, pg.position.y + 0.02 + (onPan[o] as number) * 0.07, pg.position.z);
          onPan[o] = (onPan[o] as number) + 1;
        } else if (o === -2 && hp) g.position.set(hp.x, hp.y + 0.9, hp.z + 0.15);
        else if (o === -3) {
          const pg = this.pans[W.back[i] as number]?.g;
          if (pg) g.position.set(pg.position.x, -0.9 + Math.sin(this.now * 2 + i) * 0.05, -3.5 * ROW - 0.35);
        }
      });
      if (this.crack && this.crack.at >= 0) {
        const k = Math.min(1, (this.now - this.crack.at) / 3);
        this.crack.mat.opacity = 0.25 + 0.5 * k;
        this.crack.mesh.scale.setScalar(0.25 + 0.75 * k);
      } else if (this.crack && this.crack.at === -2) {
        this.crack.mat.opacity *= 0.9;
        if (this.crack.mat.opacity < 0.02) {
          this.crack.mesh.visible = false;
          this.crack.at = -1;
        }
      }
      this.drawStays();
    } else if (this.spans.length) this.drawStays();
  }

  /** The stays from the gantry's top to each span's two ends, as they tip. */
  private drawStays(): void {
    const v = this.tmp;
    this.spans.forEach((sp, i) => {
      if (this.cineSpans && !sp.g.parent) return;
      sp.g.updateMatrixWorld(true);
      const l = this.stays[i];
      if (!l) return;
      const a = l.geometry.getAttribute('position') as THREE.BufferAttribute;
      const top = this.gantryTop;
      v.set(-0.82, 0.2, 2.3).applyMatrix4(sp.g.matrixWorld);
      a.setXYZ(0, top.x - 0.3, top.y, top.z);
      a.setXYZ(1, v.x, v.y, v.z);
      v.set(0.82, 0.2, -2.3).applyMatrix4(sp.g.matrixWorld);
      a.setXYZ(2, top.x + 0.3, top.y, top.z);
      a.setXYZ(3, v.x, v.y, v.z);
      a.needsUpdate = true;
    });
  }

  frame(now: number, s?: BattleState): void {
    this.now = now;
    if (this.lowerAt === -2) this.lowerAt = now;
    for (const m of this.timed) (m.uniforms.uTime as THREE.IUniform<number>).value = now;
    const g = this.gust;
    if (g) {
      const warn = g.warnAt >= 0 ? Math.min(1, (now - g.warnAt) / 3) : 0;
      const hit = g.hitAt >= 0 ? Math.max(0, 1 - (now - g.hitAt) / 1.2) : 0;
      g.mat.uniforms.uWarn!.value = Math.max(warn, hit * 0.8);
      g.mat.uniforms.uHit!.value = hit;
      const sd = this.spindrift;
      if (sd) {
        const k = Math.max(warn, hit);
        sd.mesh.visible = k > 0.02;
        sd.mat.uniforms.uWarn!.value = k;
        // Along the upwind flank of the platform.
        const side = -(g.dir || 1);
        sd.mesh.position.set(side * 2.9 * S, -0.2, 0);
        sd.mesh.rotation.y = Math.PI / 2;
        sd.mesh.scale.set(22 * ROW, 1.6 + k * 1.2, 1);
      }
      for (const f of this.flags) {
        let d = f.want - f.yaw;
        while (d > Math.PI) d -= Math.PI * 2;
        while (d < -Math.PI) d += Math.PI * 2;
        f.yaw += d * 0.05;
        f.g.rotation.y = f.yaw;
        f.mat.uniforms.uWild!.value = 0.4 + warn * 0.8 + hit;
      }
    }
    void s;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    for (const t of this.textures) t.dispose();
    this.gust?.tex.dispose();
  }
}

/** How far the rim is from the platform (world units): the span's length. */
const SPAN_GAP = 9.6;
/** Each of the counterweight's three spans, end to end over the drop. */
const SPAN_LEN = 4.6;
/** Where the spans meet the lip: between the left pan and the middle one (the way off the bridge passes them). */
const SPAN_X = -1.2;
/** How far out the platform stands from this side's edge (the cinematic's long view). */
const PILLAR = 13;
/** How far the gorge runs past the platform to its far wall. */
const FAR_SIDE = 36;
